import _ from "lodash";
import { NextApiRequest } from "next";
import { NextParsedUrlQuery } from "next/dist/server/request-meta";
import { NextRouter } from "next/router";

import { descriptions } from "constants/levelData";
import { en } from "constants/locale/en";
import { Regions } from "constants/regions";
import { QueryArgs } from "lib/benchmarking/types";
import {
  TitleExplorerResult,
  CompanyExplorerResult,
  TitleExplorerCompanyRollup,
  CompanyExplorerTitleRollup,
} from "lib/frontend/benchmarking/types";
import { StatsSummary } from "services/PostsService";
import { MAX_TITLE_ROLLUPS_TO_RENDER } from "utils/range_aggregators";

import { getMercerLevelSortKey } from "./utils";

export function formatNum(value: number, shouldRound: boolean): string {
  if (shouldRound) {
    return `$${Math.round((value as number) / 1000).toLocaleString("en-US")}k`;
  }
  return `$${Math.round(value as number).toLocaleString("en-US")}`;
}

export function formatRange(
  rangeLow?: number | null,
  rangeHigh?: number | null,
  opts?: { round: boolean },
): string {
  const shouldRound = opts?.round ?? true;

  if (_.isNil(rangeLow) && _.isNil(rangeHigh)) {
    return "N/A";
  }

  if (rangeLow === rangeHigh && !_.isNil(rangeLow)) {
    return formatNum(rangeLow, shouldRound);
  }

  const low = !_.isNil(rangeLow) ? formatNum(rangeLow, shouldRound) : "N/A";
  const high = !_.isNil(rangeHigh) ? formatNum(rangeHigh, shouldRound) : "N/A";

  return `${low} - ${high}`;
}

export function formatYearsExperience(low: number | null, high: number | null): string {
  if (_.isNil(low) && _.isNil(high)) {
    return "";
  } else if (_.isNil(high)) {
    return `${low}+ Years Experience`;
  } else {
    return `${low}-${high} Years Experience`;
  }
}

export function getOrdinal(n: number): string {
  let ord = "th";

  if (n % 10 === 1 && n % 100 !== 11) {
    ord = "st";
  } else if (n % 10 === 2 && n % 100 !== 12) {
    ord = "nd";
  } else if (n % 10 === 3 && n % 100 !== 13) {
    ord = "rd";
  }

  return ord;
}

export function shouldIncludeClause(
  value: string | null | undefined | string[],
): value is string | string[] {
  if (_.isNil(value)) {
    return false;
  }

  if (typeof value === "string") {
    return value !== "";
  }

  return value.length > 0;
}

export function removeOffsetParam(queryString: string): string {
  const params = queryString.slice(1).split("&");
  const filteredParams = params.filter((param) => !param.startsWith("offset"));
  return "?" + filteredParams.join("&");
}

export function getMedian(numbers: number[]): number | null {
  if (numbers.length === 0) {
    return null;
  }

  numbers.sort((a, b) => a - b);

  if (numbers.length % 2 === 1) {
    return numbers[Math.floor(numbers.length / 2)];
  } else {
    const middleIndex = numbers.length / 2;
    return (numbers[middleIndex - 1] + numbers[middleIndex]) / 2;
  }
}

export function medianBy<T>(items: T[], keyOrFn: keyof T | ((item: T) => number)): number | null {
  if (items.length === 0) {
    return null;
  }

  let values: number[];

  if (typeof keyOrFn === "function") {
    values = _.map(items, keyOrFn);
  } else {
    values = _.map(items, (item) => item[keyOrFn] as number);
  }

  for (const value of values) {
    if (typeof value !== "number") {
      throw new Error(`PIE Err: Expected value to be a number, but got ${typeof value}`);
    }
  }

  return getMedian(values);
}

export function mapDictionary<T, U>(
  dictionary: { [key: string]: T },
  fn: (value: T, key: string) => U,
): { [key: string]: U } {
  return _.mapValues(dictionary, (value, key) => fn(value, key));
}

export function padString(string: string, length: number, padChar: string): string {
  return string.length >= length ? string : string + padChar.repeat(length - string.length);
}

type ExploreQueryStringParams = keyof QueryArgs;

export function dictionaryToSearchParams(dictionary: Partial<QueryArgs>): string {
  let searchParams = new URLSearchParams();

  /**
   * a helper for static type verification
   */
  function appendSearchParams(key: ExploreQueryStringParams, value: string) {
    searchParams.append(key, encodeURIComponent(value));
  }

  const keys: ExploreQueryStringParams[] = [
    "jobFamily",
    "level",
    "subFamily",
    "specialization",
    "subsector",
    "supersector",
    "revenue",
    "employeeCount",
    "state",
    "city",
    "sector",
    "region",
    "tier",
    "Company Size",
    "Industry Segment",
  ];

  const dimensionKeys = ["state", "city", "region", "sector", "tier"];

  keys.forEach((key) => {
    const list = _.get(dictionary, key) as string[] | undefined;
    if (list) {
      _.castArray(list)
        .sort()
        .forEach((value) => appendSearchParams(key, value));
    }
  });

  return searchParams.toString();
}

export const EMPTY_QUERY_OBJECT: QueryArgs = {
  jobFamily: "",
  subFamily: "",
  specialization: "",
  subsector: "",
  supersector: "",
  revenue: "",
  employeeCount: "",
  level: "",
  state: "",
  region: "",
  city: "",
  tier: "",
  sector: "",
  dimensionType: "",
  dimensionValue: "",
};

function isEmpty(value: string | null | undefined): boolean {
  return _.isEmpty(value) || _.isNil(value);
}

export function getDimensionFromQueryArgs(args: QueryArgs): { type: string; value: string } {
  if (!isEmpty(args.dimensionType) && !isEmpty(args.dimensionValue)) {
    // @ts-expect-error TS2322
    return { type: args.dimensionType, value: args.dimensionValue };
  }

  // if (!_.isEmpty(args.region)) {
  //   return { type: "region", value: args.region };
  // }

  // if (!_.isEmpty(args.city)) {
  //   return { type: "city", value: args.city };
  // }

  // if (!_.isEmpty(args.tier)) {
  //   return { type: "tier", value: args.tier };
  // }

  // if (!_.isEmpty(args.state)) {
  //   return { type: "state", value: args.state };
  // }

  if (!isEmpty(args.sector)) {
    // @ts-expect-error TS2322
    return { type: "sector", value: args.sector };
  }

  if (!isEmpty(args.subsector)) {
    // @ts-expect-error TS2322
    return { type: "subsector", value: args.subsector };
  }

  if (!isEmpty(args.supersector)) {
    // @ts-expect-error TS2322
    return { type: "supersector", value: args.supersector };
  }

  if (!isEmpty(args.revenue)) {
    // @ts-expect-error TS2322
    return { type: "revenue", value: args.revenue };
  }

  if (!isEmpty(args.employeeCount)) {
    // @ts-expect-error TS2322
    return { type: "employeeCount", value: args.employeeCount };
  }

  if (!isEmpty(args["Company Size"])) {
    // @ts-expect-error TS2322
    return { type: "Company Size", value: args["Company Size"] };
  }

  if (!isEmpty(args["Industry Segment"])) {
    // @ts-expect-error TS2322
    return { type: "Industry Segment", value: args["Industry Segment"] };
  }

  return { type: "summary", value: "summary" };
}

export function searchParamsToDictionary(searchParams: URLSearchParams): QueryArgs {
  return {
    level: decodeURIComponent(searchParams.get("level") ?? ""),
    jobFamily: decodeURIComponent(searchParams.get("jobFamily") ?? ""),
    subFamily: decodeURIComponent(searchParams.get("subFamily") ?? ""),
    specialization: decodeURIComponent(searchParams.get("specialization") ?? ""),
    subsector: decodeURIComponent(searchParams.get("subsector") ?? ""),
    supersector: decodeURIComponent(searchParams.get("supersector") ?? ""),
    revenue: decodeURIComponent(searchParams.get("revenue") ?? ""),
    employeeCount: decodeURIComponent(searchParams.get("employeeCount") ?? ""),
    state: decodeURIComponent(searchParams.get("state") ?? ""),
    region: decodeURIComponent(searchParams.get("region") ?? ""),
    city: decodeURIComponent(searchParams.get("city") ?? ""),
    tier: decodeURIComponent(searchParams.get("tier") ?? ""),
    sector: decodeURIComponent(searchParams.get("sector") ?? ""),
    dimensionType: decodeURIComponent(searchParams.get("dimensionType") ?? ""),
    dimensionValue: decodeURIComponent(searchParams.get("dimensionValue") ?? ""),
    "Company Size": decodeURIComponent(searchParams.get("Company Size") ?? ""),
    "Industry Segment": decodeURIComponent(searchParams.get("Industry Segment") ?? ""),
  };
}

export const defaultQueryString = dictionaryToSearchParams({
  jobFamily: "ITC",
  specialization: "000",
  subFamily: "13",
});

export const cartaDefaultQueryString = dictionaryToSearchParams({
  jobFamily: "ITC",
  specialization: "000",
  subFamily: "13",
});

export const comptryxDefaultQueryString = dictionaryToSearchParams({
  jobFamily: "RS",
  specialization: "000",
  subFamily: "W",
  state: "United States",
});

export function isRegion(region: string): region is Regions {
  return Object.values(Regions).includes(region as Regions);
}

export function convertKeysToCamelCase(obj: object): object {
  const newObj: any = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const newKey = key.replace(/_([a-z])/g, (g) => g[1].toUpperCase());
      // @ts-ignore
      newObj[newKey] = typeof obj[key] === "object" ? convertKeysToCamelCase(obj[key]) : obj[key];
    }
  }
  return newObj;
}

export function getUrlFromNextRequest(req: NextApiRequest): URL | undefined {
  if (req.url) {
    return new URL(req.url, "http://dummyurl.com");
  } else {
    return undefined;
  }
}

/**
 * limits the number of titles included in each CompanyExplorerResult
 * to MAX_TITLE_ROLLUPS_TO_RENDER, some companies have too many titles and it makes the document
 * size too large in the accordion
 */
export function clipResults(results: CompanyExplorerResult[]): CompanyExplorerResult[] {
  return results.map((result) => {
    return {
      ...result,
      titles: _.slice(result.titles, 0, MAX_TITLE_ROLLUPS_TO_RENDER),
    };
  });
}

export function getQueryArgs(queryString: string): QueryArgs {
  const url = new URL(`https://comprehensive.io?${queryString}`);
  const searchParams = url.searchParams;
  return searchParamsToDictionary(searchParams);
}

export function hasPayPoint(
  result:
    | TitleExplorerResult
    | CompanyExplorerResult
    | TitleExplorerCompanyRollup
    | CompanyExplorerTitleRollup,
): boolean {
  return !!(result.salaryLow || result.salaryMid || result.salaryHigh);
}

export function getSalaryRangeOrPoint(
  result: TitleExplorerResult | CompanyExplorerResult,
  prefix?: string,
): string {
  const payPoints = [result.salaryLow ?? 0, result.salaryMid ?? 0, result.salaryHigh ?? 0].sort(
    (a, b) => (a > b ? 1 : -1),
  );

  let formatted = "";

  if (payPoints.length > 1) {
    formatted = formatRange(payPoints[0], payPoints[payPoints.length - 1], { round: true });
  } else {
    formatted = formatNum(payPoints[0], true);
  }

  if (prefix) {
    formatted = prefix + formatted;
  }

  return formatted;
}

export function getRangeOrPointFromPercentile(
  stats: StatsSummary,
  percentile: string = "50",
  prefix?: string,
): string {
  const percentiles = stats.percentiles[percentile as keyof StatsSummary["percentiles"]];

  let formatted = "";

  if (!percentiles.salaryLow || !percentiles.salaryHigh) return formatted;

  if (percentiles.salaryLow === percentiles.salaryHigh) {
    formatted = formatNum(percentiles.salaryHigh, true);
  } else {
    formatted = formatRange(percentiles.salaryLow, percentiles.salaryHigh, { round: true });
  }

  if (prefix) {
    formatted = prefix + formatted;
  }

  return formatted;
}

export function getTopResults(results: any[], prefix = "") {
  let topResults = prefix;
  const postCount = results.length < 3 ? results.length : 3;
  for (let i = 0; i < postCount; i++) {
    if (hasPayPoint(results[i])) {
      if (results[i].normalizedTitle) {
        topResults += ` ${results[i].normalizedTitle}:`;
      } else if (results[i].companyName) {
        topResults += ` ${results[i].companyName}:`;
      }
      topResults += ` ${getSalaryRangeOrPoint(results[i])}`;
      if (i !== postCount - 1) {
        topResults += ",";
      }
    }
  }

  return topResults;
}

export function getPageTitle(router: NextRouter, results: any[], stats: StatsSummary): string {
  let title = en.translations.seo_title;

  const routerQuery: NextParsedUrlQuery = router.query;
  const pathname = router.pathname;
  const queryString = routerQuery?.query?.[0] ?? "";
  const queryArgs = getQueryArgs(queryString);

  title = "Comprehensive.io | Free Salary Benchmarking Data";

  // if (pathname.includes("/t/") && routerQuery?.title) {
  //   const jobTitle = routerQuery.title;
  //   title = `${jobTitle} Salary`;
  //   // title only
  //   if (!queryArgs.companyName.length) {
  //     title += getRangeOrPointFromPercentile(stats, "50", " | ");
  //   }
  //   // title and company
  //   if (queryArgs.companyName.length === 1) {
  //     title = `${queryArgs.companyName[0]} ${title}`;
  //     if (results?.length && hasPayPoint(results[0])) {
  //       title += getSalaryRangeOrPoint(results[0], " | ");
  //     }
  //   }
  // }

  if (!title.includes("Comprehensive.io")) {
    title += " | Comprehensive.io";
  }

  return title;
}

export function getPageDescription(
  router: NextRouter,
  results: any[],
  stats: StatsSummary,
): string {
  // Set default description (will modify below if conditions permit)
  let description = en.translations.seo_description;
  const routerQuery: NextParsedUrlQuery = router.query;
  const pathname = router.pathname;
  const queryString = routerQuery?.query?.[0] ?? "";
  const queryArgs = getQueryArgs(queryString);
  const date = new Date();
  const monthYear = `${date.toLocaleString("default", { month: "long" })} ${date.getFullYear()}`;

  if (pathname.includes("/s/") && routerQuery?.title) {
    const jobTitle = routerQuery.title;
  }

  if (description !== en.translations.seo_description) {
    description += ` (as of ${monthYear}).`;
  }

  return description;
}

export function getLevelSortKey(level: string): number {
  if (level === "Other") {
    return 100;
  }

  const match = level.match(/([A-Z]+)(\d+)/);
  if (!match) {
    return 0;
  }

  const [, type, number] = match;

  if (type === "IC") {
    return 1;
  } else if (type === "M") {
    return 2;
  } else if (type === "E") {
    return 3;
  } else {
    return 0;
  }
}

export function sortLevels(levels: string[]): string[] {
  // levels are: IC1, IC2 ... ICn, E1 ... En, M1 ... Mn, or Other
  // sort IC before M before E and then by number ascending
  // put "Other" at the end

  const sortedLevels = _.sortBy(levels, getMercerLevelSortKey);

  return sortedLevels;
}

export function levelLabel(level: string): string {
  const levelDescription = descriptions[level as keyof typeof descriptions];
  if (levelDescription) {
    return `${level} - ${levelDescription.title}`;
  } else {
    return level;
  }
}

export const QUERY_ARGS_THAT_ARE_NOT_FILTERS = [
  "title",
  "jobFamily",
  "level",
  "offset",
  "sortKey",
  "sortDir",
  "activeWithin",
  "inactiveSince",
  "subJobFamily",
];

export function getSizeTrust(sampleSize: number, hasMedianValue: boolean): 0 | 1 | 2 | 3 | 4 {
  if (!hasMedianValue) {
    return 0;
  } else if (sampleSize <= 25) {
    return 1;
  } else if (sampleSize <= 50) {
    return 2;
  } else if (sampleSize <= 100) {
    return 3;
  } else {
    return 4;
  }
}

export const getNumberOfBars = (dataPointCount: number): number => {
  if (!dataPointCount) {
    return 0;
  } else if (dataPointCount === 1) {
    return 1;
  } else if (dataPointCount <= 3) {
    return 2;
  } else if (dataPointCount <= 5) {
    return 3;
  } else {
    return 4;
  }
};
